import { formatUtc } from '../../components/Async'
import type { Stat } from '../../components/charts/StatTiles'
import { exactNumber } from '../../components/charts/scale'
import type { DeviceActivityRow, FleetTopology } from '../../lib/api'

/**
 * The five numbers at the top of the dashboard, decided as data rather than as markup.
 *
 * This is a module rather than five lines inside the widget because of what it got wrong. The tiles
 * used to read the topology as `data.topology?.deviceCount ?? 0`, and chose the wording underneath
 * from `unprotected > 0`. So a projection that had not been rebuilt, a projection that was stale, and
 * a session that had expired all produced the same screen: **0 Devices**, and the words *Every machine
 * is covered* — an all-clear, printed at the moment the page knew least, beside a red banner saying the
 * session had expired.
 *
 * The rule it broke is D-263: a value that was not measured is never reported as a value that was. So
 * `null` here means the read did not arrive and renders as a dash with no note, and an empty array
 * means the read arrived and genuinely counted nothing. Those are different sentences and the
 * difference is the whole point of the file.
 *
 * Being a plain function also makes it testable, which the widget was not — the SPA has no component
 * tests, so logic that matters lives beside the component and is tested on its own (D-186's pattern).
 */
export interface FleetSummarySource {
  activity: DeviceActivityRow[] | null
  topology: FleetTopology | null
  windowDays: number
}

/** The dash used wherever a number is not known. Not a zero, and not an empty cell. */
export const NOT_MEASURED = '—'

export function fleetSummary(source: FleetSummarySource): Stat[] {
  const { activity, topology, windowDays } = source

  const devices = topology === null ? NOT_MEASURED : exactNumber(topology.deviceCount)

  // Nothing rebuilds the topology on its own, so this count can be an hour or a week old and used to
  // look identical to a live one. The panel further down the page has always carried the timestamp;
  // the tile an operator actually reads did not.
  const projectedUtc = topology?.projectedUtc ?? null

  // The note is the part that made this dangerous, so it is only ever chosen when a projection
  // actually arrived. A missing topology gets no reassurance and no warning: there is nothing to say.
  const unprotected =
    topology === null
      ? null
      : topology.subnets.reduce((sum, subnet) => sum + subnet.devicesWithNoPolicy, 0)

  const total = (pick: (row: DeviceActivityRow) => number): string =>
    activity === null ? NOT_MEASURED : exactNumber(activity.reduce((sum, row) => sum + pick(row), 0))

  const failures = activity === null ? null : activity.reduce((sum, row) => sum + row.failureCount, 0)

  return [
    {
      label: 'Devices',
      value: devices,
      note: projectedUtc === null ? undefined : `Counted ${formatUtc(projectedUtc)}`,
      to: '/devices'
    },
    {
      label: 'Carrying no policy',
      value: unprotected === null ? NOT_MEASURED : exactNumber(unprotected),
      note:
        unprotected === null
          ? undefined
          : unprotected > 0
            ? 'A gap, or a decision'
            : 'Every machine is covered',
      tone: unprotected !== null && unprotected > 0 ? 'warning' : 'plain',
      to: '/devices'
    },
    { label: `Events, ${windowDays} days`, value: total((row) => row.eventCount), to: '/cases?feed=raw' },
    // Counts detections rather than alert cases, so it leads to the screen that lists detections. It
    // pointed at Alerts until D-265, where a fold of 6,426 detections into 25 cases meant the number
    // and its destination were different units.
    { label: 'Detections', value: total((row) => row.findingCount), to: '/cases?feed=raw' },
    {
      label: 'Capability failures',
      value: total((row) => row.failureCount),
      tone: failures !== null && failures > 0 ? 'danger' : 'plain',
      to: '/cases?feed=raw'
    }
  ]
}
